import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function TrafficWidget({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cdtNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const isHomeward = cdtNow.getHours() >= 14;
  const title = isHomeward ? 'Commute Home' : 'Commute to Work';

  useEffect(() => {
    async function fetchTraffic() {
      try {
        const direction = isHomeward ? 'tohome' : 'towork';
        const res = await apiFetch(`/api/traffic?direction=${direction}`);
        const json = await res.json();

        if (json.error) {
          setError(json.error);
          return;
        }

        if (json.status && json.status !== 'OK') {
          setError(`API error: ${json.status} - ${json.error_message || ''}`);
          return;
        }

        if (json.rows && json.rows[0]?.elements?.[0]) {
          const el = json.rows[0].elements[0];
          if (el.status && el.status !== 'OK') {
            setError(`Route error: ${el.status}`);
            return;
          }
          const distanceKm = el.distance?.value || 0;
          const miles = (distanceKm / 1609.34).toFixed(1);

          setData({
            duration: el.duration?.text || '',
            durationInTraffic: el.duration_in_traffic?.text || '',
            distance: `${miles} mi`,
          });
        }
      } catch (err) {
        setError('Could not load traffic');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchTraffic();
  }, [refreshKey, isHomeward]);

  // Auto-refresh every 2 minutes between 7:30–8:45 AM CDT
  useEffect(() => {
    function isCommuteWindow() {
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
      const day = now.getDay();
      if (day === 0 || day === 6) return false; // weekends
      const mins = now.getHours() * 60 + now.getMinutes();
      return mins >= 450 && mins <= 525; // 7:30 (450) to 8:45 (525)
    }

    if (!isCommuteWindow()) return;

    const timer = setInterval(() => {
      if (isCommuteWindow()) {
        setLoading(true);
        // Re-trigger by updating a local state, but we can just call fetch directly
        const direction = isHomeward ? 'tohome' : 'towork';
        apiFetch(`/api/traffic?direction=${direction}`)
          .then(res => res.json())
          .then(json => {
            if (json.rows && json.rows[0]?.elements?.[0]) {
              const el = json.rows[0].elements[0];
              if (el.status === 'OK' || !el.status) {
                const distanceKm = el.distance?.value || 0;
                const miles = (distanceKm / 1609.34).toFixed(1);
                setData({
                  duration: el.duration?.text || '',
                  durationInTraffic: el.duration_in_traffic?.text || '',
                  distance: `${miles} mi`,
                });
              }
            }
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      } else {
        clearInterval(timer);
      }
    }, 120000); // 2 minutes

    return () => clearInterval(timer);
  }, [isHomeward]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <section className="card"><h2>{title}</h2><p className="loading">Loading...</p></section>;
  if (error) return <section className="card"><h2>{title}</h2><p className="error">{error}</p></section>;
  if (!data) return <section className="card"><h2>{title}</h2><p className="empty">No data</p></section>;

  return (
    <section className="card traffic-section">
      <h2>{title}</h2>
      <div className="traffic-info">
        <div className="traffic-stat traffic-primary">
          <span className="traffic-value-large">{data.durationInTraffic || data.duration}</span>
        </div>
        <span className="traffic-distance">{data.distance}</span>
      </div>
    </section>
  );
}

export default TrafficWidget;
